import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { Calendar as CalIcon, Sparkles, ShieldCheck, Pencil, PlusCircle } from "lucide-react";
import { Fireflies } from "@/components/effects/Fireflies";
import { Reveal } from "@/components/effects/Reveal";
import { T } from "@/context/SiteContent";
import { imgUrl } from "@/config";
import { computeRentalPrice, MIN_RENTAL_SUBTOTAL } from "@/utils/pricing";

/* ==================== Booking Widget ====================
   Slim summary + hand-off to the dedicated /checkout page.
   Address, dates, times and cart selection live here (and in TripSetup).
   Everything else — contact fields, auth choice, promo, DP / SMS / Terms
   checkboxes, payment — happens on /checkout.
*/
export const BookingWidget = ({
  products, cart, setCart, addToCart,
  cartOptions = {}, setCartOptions,
  range, zones, isOffProperty, zoneId, resort, address, addrCheck,
  deliverySlot, pickupSlot,
  differentPickup, pickupIsOffProperty, pickupZoneId, pickupResort, pickupAddress, pickupAddrCheck,
  onEditTrip,
}) => {
  const nav = useNavigate();

  const items = Object.entries(cart)
    .filter(([, q]) => q > 0)
    .map(([product_id, quantity]) => ({
      product_id,
      quantity,
      option_ids: cartOptions[product_id] || [],
    }));

  const activeZone = zones.find((z) => z.id === zoneId);

  // $50 rental-merchandise minimum — rental items only, before tax/fees/delivery.
  const rentalSubtotal = items.reduce((sum, { product_id, quantity, option_ids }) => {
    const p = products.find((x) => x.id === product_id);
    if (!p) return sum;
    const pr = computeRentalPrice(p, range);
    if (!pr) return sum;
    let optDelta = 0;
    for (const oid of option_ids || []) {
      for (const g of p.option_groups || []) {
        const o = (g.options || []).find((x) => x.id === oid);
        if (o) optDelta += Number(o.price_delta_per_day || 0);
      }
    }
    return sum + Math.floor(pr.rate + optDelta) * pr.billableDays * quantity;
  }, 0);
  const belowMin = !!(range?.from && range?.to) && items.length > 0 && rentalSubtotal < MIN_RENTAL_SUBTOTAL;
  const minShortfall = Math.ceil(MIN_RENTAL_SUBTOTAL - rentalSubtotal);

  const canProceed = (() => {
    if (!range?.from || !range?.to) return false;
    if (items.length === 0) return false;
    if (belowMin) return false;
    if (!activeZone) return false;
    if (activeZone.requires_address && !addrCheck?.delivers) return false;
    if (!activeZone.requires_address && !resort) return false;
    if (!deliverySlot || !pickupSlot) return false;
    return true;
  })();

  const handleProceed = () => {
    if (!range?.from || !range?.to) { onEditTrip?.(); return toast.error("Please choose your dates & delivery first"); }
    if (items.length === 0) return toast.error("Add at least one item to your booking");
    if (belowMin) return toast.error(`$${MIN_RENTAL_SUBTOTAL} rental minimum — add $${minShortfall} more in rental items to complete your reservation.`);
    if (!activeZone) { onEditTrip?.(); return toast.error("Please choose your delivery destination first"); }
    if (activeZone.requires_address && !addrCheck?.delivers) return toast.error("Please verify your delivery address first");
    if (!activeZone.requires_address && !resort) return toast.error("Pick your resort");
    if (!deliverySlot || !pickupSlot) { onEditTrip?.(); return toast.error("Please pick both a delivery & pickup 1-hour window"); }

    const pickupZone = differentPickup ? zones.find((z) => z.id === pickupZoneId) : null;
    nav("/checkout", {
      state: {
        cart,
        cartOptions,
        range: { from: range.from.toISOString(), to: range.to.toISOString() },
        zoneId,
        delivery_resort: activeZone.requires_address ? (address || activeZone.area) : resort,
        deliverySlot,
        pickupSlot,
        address,
        isOffProperty,
        differentPickup,
        pickup_zone_id: differentPickup ? pickupZoneId : null,
        pickup_resort: differentPickup ? (pickupZone?.requires_address ? (pickupAddress || pickupZone.area) : pickupResort) : null,
        pickup_address: differentPickup && pickupZone?.requires_address ? pickupAddress : null,
        pickupIsOffProperty,
      },
    });
  };

  const updateQty = (id, delta) => {
    setCart((c) => {
      const next = { ...c, [id]: Math.max(0, (c[id] || 0) + delta) };
      if (next[id] === 0) {
        delete next[id];
        // Also clear any variation selections for this line so a re-add
        // starts clean rather than silently reusing the last picks.
        if (setCartOptions) {
          setCartOptions((o) => {
            const nxt = { ...o };
            delete nxt[id];
            return nxt;
          });
        }
      }
      return next;
    });
  };

  return (
    <section id="book" className="section relative">
      <div className="section-tint" />
      <Fireflies count={16} />
      <div className="container">
        <Reveal>
          <p className="eyebrow mb-4"><T k="booking.eyebrow" fallback="Confirm & pay" /></p>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight max-w-3xl leading-tight md:leading-none">
            <T k="booking.headline" fallback="A few details and you're done." />
          </h2>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-[1.1fr_1fr] gap-8">
          <div className="glass p-5 sm:p-8 md:p-10">
            {range?.from && range?.to ? (
              <div
                className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4 mb-6 flex items-center justify-between gap-3"
                data-testid="booking-trip-summary"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <CalIcon className="w-4 h-4 text-[#FFD166] shrink-0" />
                  <div className="text-sm min-w-0">
                    <div className="text-[#F6F1E4] truncate">
                      <span className="text-[#FFD166] font-medium">{format(range.from, "MMM d")}</span> →{" "}
                      <span className="text-[#FFD166] font-medium">{format(range.to, "MMM d, yyyy")}</span>
                    </div>
                    <div className="text-[11px] text-[#B9C8D5] truncate">
                      {(() => {
                        if (isOffProperty && activeZone?.requires_address) return `Deliver to ${address}`;
                        if (resort) return `Deliver to ${resort}`;
                        return activeZone ? `Deliver to ${activeZone.area}` : "";
                      })()}
                      {differentPickup && (
                        <> · Pickup <span className="text-[#FFD166]">{(() => {
                          const pz = zones.find((z) => z.id === pickupZoneId);
                          if (pickupIsOffProperty && pz?.requires_address) return pickupAddress;
                          if (pickupResort) return pickupResort;
                          return pz ? pz.area : "";
                        })()}</span></>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onEditTrip}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[#FFD166]/50 bg-[#FFD166]/10 text-[#FFD166] text-[11px] uppercase tracking-wider px-3 py-1.5 hover:bg-[#FFD166]/20 hover:border-[#FFD166] transition-colors"
                  data-testid="booking-edit-trip"
                  title="Change your dates or delivery address"
                >
                  <Pencil className="w-3 h-3" /> Change dates &amp; address
                </button>
              </div>
            ) : (
              <div
                className="rounded-xl border-2 border-dashed border-[#F4A93A] bg-[#F4A93A]/5 p-5 mb-6 text-center"
                data-testid="booking-trip-missing"
              >
                <p className="text-sm text-[#F6F1E4] mb-3">
                  You haven&rsquo;t picked your dates &amp; delivery yet.
                </p>
                <button type="button" onClick={onEditTrip} className="btn-primary" data-testid="booking-open-trip-setup">
                  Choose dates &amp; delivery
                </button>
              </div>
            )}

            <p className="text-sm text-[#B9C8D5] leading-relaxed" data-testid="booking-checkout-explainer">
              You&rsquo;ll finalize your rental on the next step — a single secure checkout page with your rental summary.
            </p>

            <div className="mt-4 p-3 rounded-lg bg-[#FFD166]/10 border border-[#FFD166]/25 text-xs text-[#F6F1E4]" data-testid="delivery-notice">
              {(() => {
                if (!isOffProperty) {
                  return <><strong className="text-[#FFD166]">In-person meet:</strong> Disney requires we meet you in person for both delivery and pickup — we'll coordinate an exact meet-time within your chosen window.</>;
                }
                if (!activeZone) {
                  return <><strong className="text-[#FFD166]">Note:</strong> Choose your dates &amp; delivery above to see how it works.</>;
                }
                if (activeZone.requires_address) {
                  return <><strong className="text-[#FFD166]">In-person meet:</strong> We meet you at your rental for delivery and pickup — coordinated within your chosen window.</>;
                }
                const meetList = activeZone.meet_only_resorts || [];
                if (resort && meetList.includes(resort)) {
                  return <><strong className="text-[#FFD166]">In-person meet:</strong> {resort} doesn't offer bell-services drop-off, so we'll meet you in the lobby for both delivery and pickup within your chosen windows.</>;
                }
                return <><strong className="text-[#FFD166]">Bell services drop-off:</strong> Your gear will be waiting at your hotel's bell services desk — just ask for your Enchanted Gear Rentals delivery at check-in.</>;
              })()}
            </div>
          </div>

          <div className="glass-warm p-5 sm:p-8 md:p-10">
            <div className="flex items-center justify-between gap-2 mb-5">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#FFD166]" />
                <span className="eyebrow">Your Booking</span>
              </div>
              {range?.from && range?.to && (
                <button
                  type="button"
                  onClick={onEditTrip}
                  className="inline-flex items-center gap-1 rounded-full border border-[#FFD166]/40 text-[#FFD166] text-[10px] uppercase tracking-wider px-2.5 py-1 hover:bg-[#FFD166]/10 transition-colors"
                  data-testid="cart-edit-trip"
                  title="Change your dates or delivery address anytime"
                >
                  <Pencil className="w-3 h-3" /> Edit trip
                </button>
              )}
            </div>
            {items.length === 0 ? (
              <div className="text-[#B9C8D5] text-sm py-10 text-center">
                Add gear from the catalog below and the magic quote will glow up here.&nbsp;
              </div>
            ) : (
              <div className="space-y-4">
                {items.map(({ product_id, quantity, option_ids }) => {
                  const p = products.find((x) => x.id === product_id);
                  if (!p) return null;
                  // Resolve chosen option names + delta so the cart line
                  // reads "Zoe stroller · Snack tray (+$2/day)".
                  const chosenOptions = (option_ids || []).flatMap((oid) => {
                    for (const g of (p.option_groups || [])) {
                      const o = (g.options || []).find((x) => x.id === oid);
                      if (o) return [o];
                    }
                    return [];
                  });
                  return (
                    <div key={product_id} className="flex items-center gap-4 pb-4 border-b border-[#FFD166]/10" data-testid={`cart-item-${product_id}`}>
                      <img src={imgUrl(p.image)} alt={p.name} className="w-16 h-16 rounded-lg object-cover" />
                      <div className="flex-1">
                        <div className="font-serif text-lg">{p.name}</div>
                        {chosenOptions.length > 0 && (
                          <div className="text-[11px] text-[#FFD166] mt-0.5" data-testid={`cart-item-${product_id}-options`}>
                            with {chosenOptions.map((o) => `${o.name}${Number(o.price_delta_per_day) > 0 ? ` (+$${Number(o.price_delta_per_day).toFixed(2)}/day)` : ""}`).join(" · ")}
                          </div>
                        )}
                        <div className="text-xs text-[#B9C8D5]">${p.price_per_day}/day base</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => updateQty(product_id, -1)} className="w-7 h-7 rounded-full border border-[#FFD166]/30 text-[#FFD166]" data-testid={`decrement-${product_id}`}>−</button>
                        <span className="w-6 text-center">{quantity}</span>
                        <button onClick={() => updateQty(product_id, +1)} className="w-7 h-7 rounded-full border border-[#FFD166]/30 text-[#FFD166]" data-testid={`increment-${product_id}`}>+</button>
                      </div>
                    </div>
                  );
                })}
                <a href="#catalog" className="block">
                  <button type="button" className="btn-ghost w-full" data-testid="add-more-items-btn">
                    ↑ Add more items
                  </button>
                </a>
                {(() => {
                  const inCart = new Set(items.map((i) => i.product_id));
                  const suggestedIds = [];
                  for (const { product_id } of items) {
                    const p = products.find((x) => x.id === product_id);
                    for (const rid of (p?.related || [])) {
                      if (!inCart.has(rid) && !suggestedIds.includes(rid)) suggestedIds.push(rid);
                      if (suggestedIds.length >= 3) break;
                    }
                    if (suggestedIds.length >= 3) break;
                  }
                  const suggested = suggestedIds.map((id) => products.find((p) => p.id === id)).filter(Boolean);
                  if (suggested.length === 0) return null;
                  return (
                    <div className="mt-2 pt-4 border-t border-[#FFD166]/10" data-testid="cross-sell-strip">
                      <div className="eyebrow mb-3">You might also like ✨</div>
                      <div className="grid grid-cols-3 gap-2">
                        {suggested.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => (addToCart ? addToCart(p.id) : setCart((c) => ({ ...c, [p.id]: (c[p.id] || 0) + 1 })))}
                            className="group text-left rounded-xl border border-[#FFD166]/20 bg-black/30 hover:border-[#FFD166]/50 transition-colors p-2"
                            data-testid={`cross-sell-${p.id}`}
                            title={`Add ${p.name}`}
                          >
                            <img src={imgUrl(p.image)} alt={p.name} className="w-full aspect-square rounded-lg object-cover mb-2" />
                            <div className="text-[11px] leading-tight text-[#F6F1E4] line-clamp-2">{p.name}</div>
                            <div className="text-[10px] text-[#FFD166] mt-1">+ Add</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            <button
              onClick={handleProceed}
              disabled={!canProceed}
              className="btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="checkout-btn"
              aria-disabled={!canProceed}
            >
              Reserve &amp; Pay Securely
            </button>
            {belowMin && (
              <div className="mt-3 rounded-xl border border-[#F4A93A]/50 bg-[#F4A93A]/[0.08] p-3" data-testid="min-order-warning">
                <p className="text-xs text-[#F6F1E4]">
                  <span className="text-[#F4A93A] font-semibold">${MIN_RENTAL_SUBTOTAL} rental minimum.</span>{" "}
                  Add <span className="text-[#FFD166] font-semibold">${minShortfall}</span> more in rental items to complete your reservation.
                </p>
                {(() => {
                  const inCart = new Set(items.map((i) => i.product_id));
                  const addOns = products
                    .filter((p) => !inCart.has(p.id))
                    .sort((a, b) => Number(a.tier_1_3 ?? a.price_per_day ?? 0) - Number(b.tier_1_3 ?? b.price_per_day ?? 0))
                    .slice(0, 3);
                  if (addOns.length === 0) return null;
                  return (
                    <div className="mt-2.5 grid grid-cols-3 gap-2" data-testid="min-order-suggestions">
                      {addOns.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => (addToCart ? addToCart(p.id) : setCart((c) => ({ ...c, [p.id]: (c[p.id] || 0) + 1 })))}
                          className="group text-left rounded-lg border border-[#FFD166]/20 bg-black/30 hover:border-[#FFD166]/55 transition-colors p-1.5"
                          data-testid={`min-order-suggestion-${p.id}`}
                          title={`Add ${p.name}`}
                        >
                          <img src={imgUrl(p.image)} alt={p.name} className="w-full aspect-square rounded object-cover mb-1" />
                          <div className="text-[10px] leading-tight text-[#F6F1E4] line-clamp-2">{p.name}</div>
                          <div className="text-[9px] text-[#FFD166] mt-0.5 flex items-center gap-0.5">
                            <PlusCircle className="w-2.5 h-2.5" /> ${Math.floor(p.tier_1_3 ?? p.price_per_day ?? 0)}/day
                          </div>
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
            {!canProceed && !belowMin && (
              <p className="text-[11px] text-[#F4A93A] mt-2 text-center" data-testid="checkout-gate-hint">
                Add gear and lock in your dates &amp; delivery to continue.
              </p>
            )}
            <p className="text-xs text-[#B9C8D5] mt-3 text-center flex items-center justify-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Secured by Stripe · Cancel anytime with Enchanted Peace of Mind
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
